"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facebookAdsAdManagement = exports.facebookAdsAdManagementArgsSchema = exports.FacebookAdsAdAction = exports.facebookAdsUpdateAdResultSchema = exports.facebookAdsUpdateAdArgsSchema = exports.facebookAdsCreateAdResultSchema = exports.facebookAdsCreateAdArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Create Ad ----------
var callToActionTypeEnum = zod_1.z.enum([
    'APPLY_NOW',
    'BOOK_TRAVEL',
    'CALL_NOW',
    'CONTACT_US',
    'DONATE',
    'DOWNLOAD',
    'GET_OFFER',
    'GET_QUOTE',
    'INSTAGRAM_MESSAGE', // For Click to Instagram Direct ads - requires app_destination: INSTAGRAM_DIRECT
    'LEARN_MORE',
    'MESSAGE_PAGE', // For Click to Messenger ads
    'NO_BUTTON',
    'SHOP_NOW',
    'SIGN_UP',
    'SUBSCRIBE',
    'WATCH_MORE',
    'WHATSAPP_MESSAGE', // For Click to WhatsApp ads
]);
// Tracking specs schema matching Meta Ads API format
var trackingSpecSchema = zod_1.z.object({
    actionType: zod_1.z
        .enum([
        'leadgen_quality_conversion', // CRM events (down-funnel leads)
        'offsite_conversion', // Website events (pixel & Conversions API)
        'app_engagement', // App events (in-app actions)
        'app_install', // App install events
        'offline_conversion', // Offline events (in-store, call-center)
    ])
        .describe('Type of conversion event to track. leadgen_quality_conversion: CRM events after form submission. offsite_conversion: Website conversions via Pixel. app_engagement/app_install: Mobile app events. offline_conversion: Offline events from event sets.'),
    fbPixel: zod_1.z
        .string()
        .nullish()
        .describe('Facebook Pixel ID. Required for leadgen_quality_conversion and offsite_conversion. Use when tracking website events or CRM events via pixel.'),
    dataset: zod_1.z
        .union([zod_1.z.string(), zod_1.z.array(zod_1.z.string())])
        .nullish()
        .describe('Conversion dataset ID (for offline events) or offline event set ID. Required for leadgen_quality_conversion (CRM dataset) and offline_conversion (offline event set). Can be single string or array of strings.'),
    application: zod_1.z
        .string()
        .nullish()
        .describe('Application ID. Required for app_engagement and app_install tracking types.'),
});
// Ice breaker schema for welcome message automated questions (basic: 1-3 clickable buttons)
var iceBreakersSchema = zod_1.z
    .array(zod_1.z.object({
    title: zod_1.z.string().max(20).describe('Ice breaker question text (max 20 characters)'),
}))
    .min(1)
    .max(3)
    .describe('Automated questions users can click to start the conversation (1-3 questions)');
// Conversation question schema for advanced multi-step conversation forms (1-6 sequential questions)
var conversationQuestionSchema = zod_1.z.object({
    text: zod_1.z.string().max(160).describe('Question text shown to the user (max 160 characters)'),
    type: zod_1.z
        .enum(['multiple_choice', 'short_answer'])
        .describe('Question type: multiple_choice shows predefined options, short_answer allows free text input'),
    options: zod_1.z
        .array(zod_1.z.string().max(40))
        .min(2)
        .max(5)
        .nullish()
        .describe('Answer options for multiple_choice questions (2-5 options, max 40 characters each). Required when type is multiple_choice.'),
    automatedResponse: zod_1.z
        .string()
        .max(300)
        .nullish()
        .describe('Optional automated response sent after user answers this question (max 300 characters). Use to acknowledge answer or provide context before next question.'),
});
var conversationQuestionsSchema = zod_1.z
    .array(conversationQuestionSchema)
    .min(1)
    .max(6)
    .describe('Sequential conversation questions for lead qualification (1-6 questions recommended). Questions are presented one at a time in Instagram Direct/Messenger.');
// Welcome message schema for Instagram Direct, Messenger, WhatsApp ads
var welcomeMessageSchema = zod_1.z
    .object({
    greeting: zod_1.z
        .string()
        .max(160)
        .describe('Welcome message text shown to users when they click the ad (max 160 characters recommended). Example: "Hi! Thanks for your interest. How can we help you?"'),
    iceBreakers: iceBreakersSchema
        .nullish()
        .describe('Simple ice breaker questions (1-3 clickable buttons, max 20 chars each). Use for quick lead qualification. Mutually exclusive with conversationQuestions.'),
    conversationQuestions: conversationQuestionsSchema
        .nullish()
        .describe('Advanced sequential conversation questions (1-6 questions). Each question is presented one at a time with optional multiple choice or short answer. Use for detailed lead qualification. Mutually exclusive with iceBreakers.'),
})
    .refine(function (data) { return !(data.iceBreakers && data.conversationQuestions); }, {
    message: 'Cannot use both iceBreakers and conversationQuestions. Use iceBreakers for simple 1-3 button questions, or conversationQuestions for advanced multi-step flows.',
    path: ['iceBreakers'],
})
    .describe('Welcome message configuration for Instagram Direct, Messenger, or WhatsApp ads. Creates an automated greeting with either simple ice breakers (1-3 buttons) OR advanced conversation questions (1-6 sequential questions) for lead qualification.');
// Messaging flow reference schema - for pre-created flows via Messenger Platform API or third-party tools
var messagingFlowSchema = zod_1.z.object({
    flowId: zod_1.z
        .string()
        .describe('ID of a pre-created messaging flow (partner_app_welcome_message_flow_id). Flows are created via Messenger Platform API, Meta Business Suite, or third-party tools like Chatfuel/ManyChat. Use for complex multi-step automation with conditional logic.'),
});
var sharedCreativeFields = zod_1.z.object({
    urlParameters: zod_1.z
        .record(zod_1.z.string(), zod_1.z.string())
        .nullish()
        .describe('URL parameters appended by Meta at click time via ad_url_parameters (keeps destination URL clean). Provide key-value pairs; values can be string/number/boolean. Common: utm_source, utm_medium, utm_campaign, utm_content, placement macros like {{placement}}.'),
    trackingSpecs: zod_1.z
        .array(trackingSpecSchema)
        .nullish()
        .describe('Conversion tracking specifications. Controls the tracking checkboxes in Meta Ads Manager (CRM events, Website events, App events, Offline events). Set at ad level as tracking_specs. Each spec requires valid IDs: leadgen_quality_conversion requires fbPixel or dataset, offsite_conversion requires fbPixel, app_engagement/app_install require application, offline_conversion requires dataset. CRITICAL: If user requests tracking but pixel/dataset IDs are not provided, ALWAYS fetch them first using facebookAdsListPixels tool before creating the ad. Never skip tracking specs just because IDs are missing - fetch them automatically. If null or missing IDs are provided, validation will fail.'),
    welcomeMessage: welcomeMessageSchema
        .nullish()
        .describe('Welcome message with ice breakers or conversation questions for click-to-message ads (Instagram Direct, Messenger, WhatsApp). Requires CTA type: INSTAGRAM_MESSAGE, MESSAGE_PAGE, or WHATSAPP_MESSAGE. Choose iceBreakers for simple 1-3 button questions, or conversationQuestions for advanced 1-6 sequential questions with multiple choice/short answer.'),
    messagingFlow: messagingFlowSchema
        .nullish()
        .describe('Reference to a pre-created messaging flow for advanced automation. Use when you have a flow created via Messenger Platform API, Meta Business Suite, or third-party tools. Provides complex multi-step conversations with conditional logic. Mutually exclusive with welcomeMessage.'),
});
// Link Ad Creative - single image ad with link (uses object_story_spec.link_data)
var linkAdCreative = sharedCreativeFields.extend({
    type: zod_1.z.literal('link').describe('Creative type: single image/link ad'),
    name: zod_1.z.string().describe('Creative name'),
    link: zod_1.z.string().url().describe('Destination URL for the ad (cannot be a Facebook link)'),
    callPhoneNumber: zod_1.z
        .string()
        .regex(/^\+?[0-9]{6,20}$/, 'Provide E.164-style phone number, e.g. +15551234567')
        .nullish()
        .describe('Phone number for CALL_NOW ads (E.164 format, e.g. +15551234567). Use when callToActionType is CALL_NOW; ignored otherwise.'),
    message: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Primary text above the image (up to 125 characters recommended for optimal performance). This is the main ad copy that appears in the feed.'),
    headline: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('Array of ad headlines - main attention grabbers displayed prominently (up to 40 characters each recommended). Appears below the image. Facebook will optimize which headline performs best. Provide 1-5 variations for best results.'),
    description: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('Array of description texts below headline (up to 30 characters each recommended). Provides extra context about your offer. Facebook will optimize which description performs best. Provide 1-5 variations.'),
    caption: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Link caption. Must be the same as the link/destination URL for the ad.'),
    imageHash: zod_1.z
        .string()
        .nullish()
        .describe('Facebook image hash from existing creative (obtained from facebookAdsGetCreative). Use instead of images array to reuse existing image without re-uploading. Ideal for duplicating ads with modifications to text fields only. Mutually exclusive with images array.'),
    images: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url().describe('Image URL from user uploads'),
        placementType: zod_1.z
            .enum(['square', 'landscape', 'story', 'portrait', 'unknown'])
            .describe('Placement type. Use "unknown" as default (95% of cases) - system auto-matches based on actual dimensions. Only use specific types when user explicitly describes aspect ratio.'),
    }))
        .nullish()
        .describe('CRITICAL: Include ALL uploaded images in this ONE array. If user uploads 3 images for "an ad", create ONE ad with all 3 images here (NOT 3 separate ads). Each image needs url and placementType. Default placementType to "unknown" - system will auto-match to square/landscape/story/portrait based on actual dimensions. Multiple images in one ad = optimal performance across Feed/Stories/Reels. Mutually exclusive with imageHash.'),
    callToActionType: callToActionTypeEnum
        .nullable()
        .optional()
        .describe('Call to action button type. Choose based on ad type: OUTCOME_TRAFFIC → LEARN_MORE, OUTCOME_SALES → SHOP_NOW, OUTCOME_LEADS → SIGN_UP/GET_QUOTE, CALL_NOW for click-to-call, Click to Instagram Direct → INSTAGRAM_MESSAGE, Click to Messenger → MESSAGE_PAGE, Click to WhatsApp → WHATSAPP_MESSAGE. Leave empty for no button.'),
});
// Carousel Ad Creative - multiple cards that users can swipe through
var carouselCardSchema = zod_1.z.object({
    link: zod_1.z.string().url().describe('Destination URL for this card (cannot be a Facebook link)'),
    headline: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('Array of headlines for this card (up to 40 characters each recommended). Appears below the image on each card. Facebook will optimize which headline performs best. Provide 1-5 variations.'),
    description: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('Array of descriptions for this card (up to 20 characters each recommended for carousel cards). Facebook will optimize which description performs best. Provide 1-5 variations.'),
    imageHash: zod_1.z
        .string()
        .nullish()
        .describe('Facebook image hash from existing creative. Use to reuse existing image. Mutually exclusive with images array.'),
    images: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url().describe('Image URL from user uploads'),
        placementType: zod_1.z
            .enum(['square', 'landscape', 'story', 'portrait', 'unknown'])
            .describe('Placement type. Default to "unknown" - system auto-matches based on actual dimensions.'),
    }))
        .nullish()
        .describe('Array of images with placement types for this carousel card. Include all images for this card with placementType set to "unknown" (system will auto-match). Mutually exclusive with imageHash.'),
    callToActionType: callToActionTypeEnum
        .nullable()
        .optional()
        .describe('Call to action button type for this card'),
});
var carouselAdCreative = sharedCreativeFields.extend({
    type: zod_1.z
        .literal('carousel')
        .describe('Creative type: carousel ad with multiple cards. Best for showcasing multiple products, features, or telling a story. Users can swipe through cards.'),
    name: zod_1.z.string().describe('Creative name'),
    message: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Primary text above the carousel (up to 125 characters recommended). This text appears above all cards.'),
    cards: zod_1.z
        .array(carouselCardSchema)
        .min(2)
        .max(10)
        .describe('Carousel cards (minimum 2, maximum 10 per Meta specs). Each card should highlight different products or features with consistent aspect ratios. Use headlines to differentiate each card.'),
});
// Video Ad Creative - video ad with call to action
var videoAdCreative = sharedCreativeFields.extend({
    type: zod_1.z
        .literal('video')
        .describe('Creative type: video ad. Best for product demonstrations, storytelling, and engagement. Recommended: 1:1 (square) or 4:5 (vertical) aspect ratio, 1080x1080px minimum, 15-60 seconds length. Add captions (85% watch without sound).'),
    name: zod_1.z.string().describe('Creative name'),
    videoId: zod_1.z
        .string()
        .nullish()
        .describe('Facebook video ID from existing creative (obtained from facebookAdsGetCreative). Use instead of videoUrl to reuse existing video without re-uploading. Ideal for duplicating video ads with modifications to text fields only. Mutually exclusive with videoUrl.'),
    videoUrl: zod_1.z
        .string()
        .url()
        .nullish()
        .describe('Video file URL to upload to Facebook (MP4, MOV, or GIF format, max 4GB). Mutually exclusive with videoId.'),
    imageUrl: zod_1.z
        .string()
        .url()
        .nullish()
        .describe('Thumbnail/cover image URL for the video (minimum 1080x1080px recommended). Facebook requires either image_hash or image_url in video_data - provide this field to avoid ad creation failures.'),
    callPhoneNumber: zod_1.z
        .string()
        .regex(/^\+?[0-9]{6,20}$/, 'Provide E.164-style phone number, e.g. +15551234567')
        .nullish()
        .describe('Phone number for CALL_NOW video ads (E.164 format, e.g. +15551234567). Use when callToActionType is CALL_NOW; ignored otherwise.'),
    message: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Primary text above the video (up to 125 characters recommended)'),
    headline: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('Array of video headlines (up to 40 characters each recommended). Displayed prominently with the video. Facebook will optimize which headline performs best. Provide 1-5 variations.'),
    title: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Video title/headline (deprecated - use headline instead for consistency)'),
    description: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('NOT SUPPORTED for video ads. Facebook does not support description field in video_data. Use message (primary text) or headline fields instead. This field is ignored for video creatives.'),
    callToActionType: callToActionTypeEnum
        .nullable()
        .optional()
        .describe('Call to action button type. Required if callToActionLink is provided. Choose based on objective: LEARN_MORE, SHOP_NOW, WATCH_MORE.'),
    callToActionLink: zod_1.z
        .string()
        .url()
        .describe('Destination URL for the call to action button (cannot be a Facebook link). Required if callToActionType is provided, except when callToActionType is CALL_NOW and callPhoneNumber is supplied.')
        .nullable()
        .optional(),
});
// Note: Either videoId or videoUrl must be provided, but this is validated at runtime in the API service
// to avoid breaking the discriminatedUnion schema structure
// Catalog Ad Creative - dynamic product ads using product catalog (Advantage+ catalog ads)
var catalogAdCreative = sharedCreativeFields.extend({
    type: zod_1.z
        .literal('catalog')
        .describe('Creative type: Advantage+ catalog ads (dynamic product ads). Automatically shows products from your catalog to interested users. Best for e-commerce with product feeds.'),
    name: zod_1.z.string().describe('Creative name'),
    productSetId: zod_1.z
        .string()
        .describe('Product set ID from your Facebook catalog. Defines which products to show in the dynamic ad.'),
    message: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Primary text above the product ad (up to 125 characters recommended)'),
    headline: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('Array of ad headline templates (up to 40 characters each recommended). Can use dynamic parameters like {{product.name}}. Facebook will optimize which headline performs best. Provide 1-5 variations.'),
    description: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('Array of description templates (up to 30 characters each recommended). Can use dynamic parameters. Facebook will optimize which description performs best. Provide 1-5 variations.'),
    link: zod_1.z
        .string()
        .url()
        .nullable()
        .optional()
        .describe('Destination URL template for catalog ads. Can use dynamic parameters like {{product.link}}. If omitted, uses product link from catalog.'),
    callToActionType: callToActionTypeEnum
        .nullable()
        .optional()
        .describe('Call to action button type for catalog ads. Recommended: SHOP_NOW for e-commerce products.'),
});
// Collection Ad Creative - immersive mobile format showcasing multiple products
var collectionAdCreative = sharedCreativeFields.extend({
    type: zod_1.z
        .literal('collection')
        .describe('Creative type: Collection ad. Mobile-first immersive format with cover media and product grid. Opens instant experience when tapped. Best for product catalogs and visual storytelling.'),
    name: zod_1.z.string().describe('Creative name'),
    message: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Primary text above the collection (up to 125 characters recommended)'),
    link: zod_1.z
        .string()
        .url()
        .describe('Destination URL for the collection instant experience (cannot be a Facebook link)'),
    coverMedia: zod_1.z
        .object({
        type: zod_1.z.enum(['image', 'video']).describe('Cover media type: image or video'),
        url: zod_1.z
            .string()
            .url()
            .describe('Cover media URL. For image: 1200x628px recommended. For video: 1:1 or 16:9 aspect ratio, 1080x1080px minimum.'),
    })
        .describe('Cover media for collection ad. This is the main visual that appears at the top of the collection.'),
    productSetId: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Product set ID from catalog to auto-populate collection items. If omitted, requires manual product configuration.'),
    headline: zod_1.z
        .array(zod_1.z.string())
        .nullable()
        .optional()
        .describe('Array of collection headlines (up to 40 characters each recommended). Facebook will optimize which headline performs best. Provide 1-5 variations.'),
    callToActionType: callToActionTypeEnum
        .nullable()
        .optional()
        .describe('Call to action button type. Recommended for collections: SHOP_NOW, LEARN_MORE, VIEW_MORE.'),
});
// Instant Form Ad Creative - lead generation ad with Instant Form (Lead Ads)
var instantFormAdCreative = sharedCreativeFields.extend({
    type: zod_1.z
        .literal('instantForm')
        .describe('Creative type: Instant Form (Lead Ads). Collects leads directly within Facebook/Instagram without redirecting to external landing pages. Requires OUTCOME_LEADS campaign objective and LEAD_GENERATION ad set optimization.'),
    name: zod_1.z.string().describe('Creative name'),
    leadgenFormId: zod_1.z
        .string()
        .describe('ID of the existing Instant Form (Lead Form) to use. Forms can be created using facebookAdsLeadgenFormManagement tool or in Facebook Ads Manager.'),
    link: zod_1.z
        .string()
        .url()
        .nullish()
        .describe("Link URL for the instant form creative. If not provided, will use Landing Page URL from your Business Profile. This URL is used for policy compliance and fallback navigation, typically set to 'https://fb.me/' or your business website."),
    message: zod_1.z
        .string()
        .nullish()
        .describe('Primary text above the form (up to 125 characters recommended)'),
    headline: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('Array of ad headlines (up to 40 characters each recommended). Appears prominently above the form button. Facebook will optimize which headline performs best. Provide 1-5 variations.'),
    description: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('Array of descriptions (up to 30 characters each recommended). Provides context about the form. Facebook will optimize which description performs best. Provide 1-5 variations.'),
    imageHash: zod_1.z
        .string()
        .nullish()
        .describe('Facebook image hash from existing creative (obtained from facebookAdsGetCreative). Use instead of images array to reuse existing image without re-uploading. Ideal for duplicating instant form ads with modifications to form or text fields only. Mutually exclusive with images array.'),
    images: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url().describe('Image URL from user uploads'),
        placementType: zod_1.z
            .enum(['square', 'landscape', 'story', 'portrait', 'unknown'])
            .describe('Placement type. Use "unknown" as default (95% of cases) - system auto-matches based on actual dimensions. Only use specific types when user explicitly describes aspect ratio.'),
    }))
        .nullish()
        .describe('CRITICAL: Include ALL uploaded images in this ONE array. If user uploads multiple images for instant form ad, include all with placementType set to "unknown" (system will auto-match). Multiple images in one ad = optimal performance across Feed/Stories/Reels. Mutually exclusive with imageHash.'),
    callToActionType: callToActionTypeEnum
        .nullish()
        .describe('Call to action button type. Recommended for instant form ads: SIGN_UP, GET_QUOTE, APPLY_NOW, LEARN_MORE, DOWNLOAD.'),
});
var adCreative = zod_1.z.discriminatedUnion('type', [
    linkAdCreative,
    carouselAdCreative,
    videoAdCreative,
    catalogAdCreative,
    collectionAdCreative,
    instantFormAdCreative,
]);
exports.facebookAdsCreateAdArgsSchema = zod_1.z
    .object({
    adSetId: zod_1.z.string().describe('Ad set ID where ad will be created'),
    name: zod_1.z.string().describe('Ad name'),
    status: zod_1.z.enum(['ACTIVE', 'PAUSED']).default('PAUSED').describe('Ad status (default: PAUSED)'),
    creativeId: zod_1.z
        .string()
        .nullish()
        .describe('Existing Facebook creative ID to reuse (exact duplication without creating new creative). Mutually exclusive with creative field. Use for exact ad duplication when you want to preserve the same creative across multiple ads or ad sets. Cost savings: 1 credit vs 2 credits when creating new creative.'),
    creative: adCreative
        .nullish()
        .describe('Creative specification for new or modified ad creative. Mutually exclusive with creativeId. Use when creating new creative or duplicating with modifications (different headline, CTA, etc).'),
})
    .refine(function (data) {
    var hasCreativeId = data.creativeId != null;
    var hasCreative = data.creative != null;
    // Exactly one must be provided (XOR)
    return hasCreativeId !== hasCreative;
}, {
    message: 'Must provide either creativeId (for exact duplication) or creative (for new/modified creative), but not both',
});
exports.facebookAdsCreateAdResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Ad ID'),
    name: zod_1.z.string(),
    status: zod_1.z.string(),
    created: zod_1.z.boolean().default(true),
    adAccountId: zod_1.z.string().nullish().describe('Ad account ID used to create the ad'),
});
// ---------- Update Ad ----------
exports.facebookAdsUpdateAdArgsSchema = zod_1.z.object({
    adId: zod_1.z.string().describe('Ad ID to update'),
    name: zod_1.z.string().nullable().optional().describe('New ad name'),
    status: zod_1.z.enum(['ACTIVE', 'PAUSED', 'DELETED']).nullable().optional().describe('New ad status'),
});
exports.facebookAdsUpdateAdResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Ad ID'),
    updated: zod_1.z.boolean().default(true),
    success: zod_1.z.boolean(),
    adAccountId: zod_1.z.string().nullish().describe('Ad account ID used to update the ad'),
});
// ==========================
// Compound Tool: facebookAdsAdManagement
// ==========================
exports.FacebookAdsAdAction = zod_1.z.enum(['createAd', 'updateAd']);
exports.facebookAdsAdManagementArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({ name: zod_1.z.literal('createAd'), params: exports.facebookAdsCreateAdArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('updateAd'), params: exports.facebookAdsUpdateAdArgsSchema }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var FacebookAdsAdManagementAllResultsSchema = zod_1.z.union([
    exports.facebookAdsCreateAdResultSchema,
    exports.facebookAdsUpdateAdResultSchema,
]);
var facebookAdsAdManagementResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'createAd' }, schema: exports.facebookAdsCreateAdResultSchema },
        { when: { 'action.name': 'updateAd' }, schema: exports.facebookAdsUpdateAdResultSchema },
    ],
    defaultSchema: FacebookAdsAdManagementAllResultsSchema,
});
exports.facebookAdsAdManagement = (0, common_1.createToolMetadata)({
    id: 'facebookAdsAdManagement',
    displayName: 'Facebook Ad Management',
    displayDescription: 'Create and update Facebook Ads',
    category: 'ads',
    color: common_1.BRAND_COLORS.META,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.META),
    icon: 'meta',
    argsSchema: exports.facebookAdsAdManagementArgsSchema,
}, facebookAdsAdManagementResultSchemas);
